import { css, cx } from '@linaria/core';
import { CollapsibleSection } from 'components/CollapsibleSection';
import { ExtendedLinkProps, Link } from 'components/Link';
import { usePathWithoutQueryParams } from 'components/Navbar/Navbar.hooks';
import { useRouter } from 'next/router';
import { ellipsis } from 'polished';
import { baseTheme } from 'styles/utils';
interface MenuStackProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}
interface MenuGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  visible?: boolean;
  /**
   * When router's asPath (without query-params) matches this path or either of the paths, it will be expanded by default.
   * This is useful for the sidebar to expand the group when the user is on a page that belongs to this group.
   */
  highlightOnHref?: string | string[];
  /**
   * If true, the menu group will be expanded by default.
   */
  expandedByDefault?: boolean;
}
interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  href?: string;
  target?: ExtendedLinkProps['target'];
  rightSection?: React.ReactNode;
  visible?: boolean;
  /**
   * If true, removes the space allocated for the icon (20px) when no icon is provided.
   * @default false
   */
  removeIconSpace?: boolean;
  /**
   * When router's asPath (without query-params) matches this path or either of the paths, it will be expanded by default.
   * This is useful for the sidebar to expand the group when the user is on a page that belongs to this group.
   */
  highlightOnHref?: string | string[];
  /**
   * Standalone menu item without any grouping. Makes it's text appear bold.
   */
  standalone?: boolean;
  /**
   * If true, the menu item will be highlighted if the current path starts with the href (ignoring query-params).
   */
  matchInitialHrefForHighlighting?: boolean;
}
export const MenuStack: React.FC<MenuStackProps> = ({
  className,
  ...props
}) => {
  return <div className={cx(MenuStyles.stack, 'menu-stack', className)} {...props} />;
};

/**
 * Collapsible menu group component for the sidebar.
 */
export const MenuGroup: React.FC<MenuGroupProps> = ({
  title,
  icon,
  expandedByDefault,
  highlightOnHref,
  children,
  visible = true,
  className,
  ...props
}) => {
  const isHighlighted = useIsHighlighted();
  const listOfHighlightOnHref = Array.isArray(highlightOnHref) ? highlightOnHref : [highlightOnHref];
  const isExpandedByDefault = listOfHighlightOnHref.filter(Boolean).some(href => isHighlighted({
    href,
    matchInitial: true,
    usePathName: true
  })) || expandedByDefault;
  const header = <div className={cx(MenuStyles.groupHeader, 'menu-group-header')}>
      {icon}
      <span className="content">{title}</span>
    </div>;
  if (!visible) {
    return null;
  }
  return <CollapsibleSection header={header} caretPosition="right" data-selected={isExpandedByDefault} isCollapsed={!isExpandedByDefault} className={cx(MenuStyles.group, 'menu-group', className)} caretIconSize={20} headerContainerClassName="menu-group-header-container" caretIconClassName="menu-group-caret-icon" contentContainerClassName="menu-group-content-container" {...props}>
      {children}
    </CollapsibleSection>;
};
export const MenuItem: React.FC<MenuItemProps> = ({
  children,
  icon,
  href = '',
  target = '_self',
  highlightOnHref = [],
  visible = true,
  rightSection,
  standalone,
  removeIconSpace = false,
  matchInitialHrefForHighlighting = false,
  className,
  ...props
}) => {
  const isHighlighted = useIsHighlighted();
  const listOfHighlightOnHref = Array.isArray(highlightOnHref) ? highlightOnHref.concat(href) : [highlightOnHref, href];
  const isLinkHighlighted = listOfHighlightOnHref.filter(Boolean).some(_href => isHighlighted({
    href: _href,
    matchInitial: matchInitialHrefForHighlighting
  }));
  if (!visible) {
    return null;
  }
  return <Link className={cx(MenuStyles.item, 'menu-item', isLinkHighlighted && 'highlighted', standalone && 'standalone', className)} href={href} target={target} {...props}>
      {!removeIconSpace && <div className={MenuStyles.iconWrapper}>{icon}</div>}
      <span className="content">{children}</span>
      {rightSection}
    </Link>;
};
function useIsHighlighted() {
  const pathname = useRouter().pathname;
  const pageURLWithoutQueryParams = usePathWithoutQueryParams();
  return ({
    href,
    matchInitial,
    usePathName
  }: {
    href?: string;
    matchInitial?: boolean;
    usePathName?: boolean;
  }) => {
    const hrefWithoutQueryParams = href?.split('?')[0];
    const matchWithURL = usePathName ? pathname : pageURLWithoutQueryParams;
    if (hrefWithoutQueryParams && matchInitial) {
      return matchWithURL.startsWith(hrefWithoutQueryParams);
    }
    return matchWithURL === hrefWithoutQueryParams;
  };
}
export const Menu = {
  Stack: MenuStack,
  Group: MenuGroup,
  Item: MenuItem
};
export const MenuStyles = {
  stack: "omponents__SidebarV2__Menu_tsx-stack-s1gg10k5",
  group: "omponents__SidebarV2__Menu_tsx-group-gjfgp96",
  groupHeader: "omponents__SidebarV2__Menu_tsx-groupHeader-g1h3ijkh",
  item: "omponents__SidebarV2__Menu_tsx-item-ibx413g",
  iconWrapper: "omponents__SidebarV2__Menu_tsx-iconWrapper-i1u9ptea"
};

require("./Menu.linaria.module.css!=!../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./Menu.tsx");